/**
 * TdabcPrintDocument — impression PDF du hub « Coût horaire (CCR) » (People).
 * Refonte 2026-07-14 : le PDF reflète l'écran actuel, un écran par page, tout en
 * français. Ordre figé (décision Olivier) :
 *   P1 — Capacité horaire (CCR) · Synthèse   (tuiles + coût horaire de capacité)
 *   P2 — Capacité horaire (CCR) · Détail par personne  (peut couvrir 2 pages)
 *   P3 — Affectation personnel
 *   P4 — Affectation frais
 * Les onglets « Temps standard » et « Groupe / Paramétrage » sont exclus du PDF.
 *
 * Gabarit d'impression FIABLE (identique aux rapports Finance) : overlay portalé
 * dans <body> + pagination native CSS Paged Media (PeoplePrint.css). Chaque écran
 * démarre une page ; une table longue déborde naturellement sur la page suivante.
 * Réutilise le COMPOSANT RÉEL de l'écran (CostAssignmentPage) : la vue Total est
 * scindée synthèse / détail via `totalSubview`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useP } from '../../i18n/useP';
import CostAssignmentPage from './CostAssignmentPage';
import '../hr/print/PeoplePrint.css';
import '../hr/PeoplePrintDocument.css';
import '../financials/financialsScreenPrint.css';

function ReportHeader({ category, title, tenant }) {
  const p = useP();
  const dateStr = new Date().toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return (
    <header className="ppl-print__report-header">
      <div>
        <div className="ppl-print__report-eyebrow">{category}</div>
        <h2 className="ppl-print__report-title">{title}</h2>
      </div>
      <div className="ppl-print__report-meta">
        <div><strong>{tenant?.name}</strong></div>
        <div>{p('Édité le ')}{dateStr}</div>
      </div>
    </header>
  );
}

export default function TdabcPrintDocument({ activeTenant, year, unit = 'kchf', onClose }) {
  const { t } = useTranslation();
  const p = useP();
  const [ready, setReady] = useState(false);
  const noop = () => {};

  const PAGES = useMemo(() => [
    { key: 'ccr-synth',  title: t('phrases:p_af97dba81c'),
      node: <CostAssignmentPage embedded pageView="total" totalSubview="synth" unit={unit} activeTenant={activeTenant} addToast={noop} year={year} /> },
    { key: 'ccr-detail', title: t('phrases:p_7861f51087'),
      node: <CostAssignmentPage embedded pageView="total" totalSubview="detail" unit={unit} activeTenant={activeTenant} addToast={noop} year={year} /> },
    { key: 'personnel',  title: p('Affectation personnel'),
      node: <CostAssignmentPage embedded pageView="personnel" unit={unit} activeTenant={activeTenant} addToast={noop} year={year} /> },
    { key: 'frais',      title: p('Affectation frais'),
      node: <CostAssignmentPage embedded pageView="frais" unit={unit} activeTenant={activeTenant} addToast={noop} year={year} /> },
  ], [t, p, unit, activeTenant, year]);

  useEffect(() => {
    document.body.classList.add('people-printing');
    return () => document.body.classList.remove('people-printing');
  }, []);

  useEffect(() => {
    const prev = document.title;
    const label = [activeTenant?.name, year].filter(Boolean).join(' — ');
    document.title = `${p('Coût horaire (CCR)')}${label ? ` — ${label}` : ''}`;
    return () => { document.title = prev; };
  }, [activeTenant?.name, year, p]);

  useEffect(() => {
    const handler = () => onClose();
    window.addEventListener('afterprint', handler);
    return () => window.removeEventListener('afterprint', handler);
  }, [onClose]);

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const handlePrint = () => window.print();

  return createPortal(
    <div className="ppl-print-overlay">
      <div className="ppl-print-controls">
        <div className="ppl-print-controls__left">
          <span className="ppl-print-controls__title">
            {p('Aperçu — Coût horaire (CCR)')} · {activeTenant?.name}
          </span>
          {!ready && <span className="ppl-print-controls__loading">{p('⟳ Chargement des données…')}</span>}
        </div>
        <div className="ppl-print-controls__right">
          <button
            className="ppl-print-btn ppl-print-btn--print"
            onClick={handlePrint}
            disabled={!ready}
            title={ready ? p('Imprimer ou enregistrer en PDF') : p('Patientez…')}
          >
            {ready ? p('⎙ Imprimer / PDF') : p('⟳ Chargement…')}
          </button>
          <button className="ppl-print-btn ppl-print-btn--close" onClick={onClose}>{p('✕ Fermer')}</button>
        </div>
      </div>

      <div className="ppl-print__preview">
        <div className="ppl-print">
          {PAGES.map(({ key, title, node }) => (
            <section key={key} className="ppl-print__section ppl-print__section--wide fin-screenprint">
              <ReportHeader category={t('phrases:p_fb8b772ea1')} title={title} tenant={activeTenant} />
              {node}
            </section>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
